class StickyHeader extends HTMLElement {
  constructor() {
    super();

    // Additional properties to store initial colors
    this.initialStrokeColor = "";
    this.initialFillColor = "";
    this.initialBubbleColor = ""; // Store initial cart bubble color
    this.initialBubbleBackgroundColor = "";
    this.desktopSearch = document.querySelector("#desktopSearch");
  }

  connectedCallback() {
    this.header = document.querySelector(".section-header");
    this.headerWrapper = document.querySelector(".header-wrapper");
    this.headerIsAlwaysSticky =
      this.getAttribute("data-sticky-type") === "always" ||
      this.getAttribute("data-sticky-type") === "reduce-logo-size";
    this.stickyFilter = document.querySelector(
      ".collection-product-grid .facetsActionbar",
    );
    this.headerBounds = {};

    this.setHeaderHeight();

    window
      .matchMedia("(max-width: 990px)")
      .addEventListener("change", this.setHeaderHeight.bind(this));

    // Initial call to set the correct class based on the current viewport
    this.updateHeaderClass();
    this.initIconColors();

    // Event listener for window resize
    window.addEventListener("resize", this.updateHeaderClass.bind(this));

    if (this.headerIsAlwaysSticky) {
      this.header.classList.add("shopify-section-header-sticky");
    }

    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.predictiveSearch = this.querySelector("predictive-search");

    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

    this.addEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);
    window.addEventListener("scroll", this.onScrollHandler, false);

    this.createObserver();
    this.detectAndApplyHoverEffects();

    this.desktopSearchInput = this.querySelector('input[type="search"]');
    if (this.desktopSearchInput) {
      this.desktopSearchInput.addEventListener(
        "focus",
        this.showDesktopSearch.bind(this),
      );
      this.desktopSearchInput.addEventListener(
        "input",
        this.showDesktopSearch.bind(this),
      );
    }
  }

  setHeaderHeight() {
    document.documentElement.style.setProperty(
      "--header-height",
      `${this.header.offsetHeight}px`,
    );
    const announcementBar = document.querySelector("slideshow-component");
    if (announcementBar) {
      document.documentElement.style.setProperty(
        "--Announcement_Height",
        `${announcementBar.offsetHeight}px`,
      );
    }
  }

  updateHeaderClass() {
    const viewportWidth = window.innerWidth;
    const colorDesktop = this.headerWrapper.dataset.colorDesktop;
    const colorMobile = this.headerWrapper.dataset.colorMobile;
    const transparentDesktop =
      this.headerWrapper.dataset.transparentDesktop === "true";
    const transparentMobile =
      this.headerWrapper.dataset.transparentMobile === "true";
    const iconColorDesktop = this.headerWrapper.dataset.iconDesktop;
    const iconColorMobile = this.headerWrapper.dataset.iconMobile;

    // Remove existing color and transparency classes to prevent conflicts
    this.headerWrapper.classList.remove(
      colorDesktop,
      colorMobile,
      "transparent",
    );

    // Add the correct color class based on the viewport width and apply transparency if applicable
    if (viewportWidth >= 1000) {
      // Assuming 1000px as the breakpoint for desktop
      this.headerWrapper.classList.add(colorDesktop);
      if (transparentDesktop) {
        this.headerWrapper.classList.add("transparent");
      }
      this.updateIconColors(iconColorDesktop); // Set icon colors for desktop
    } else {
      this.headerWrapper.classList.add(colorMobile);
      if (transparentMobile) {
        this.headerWrapper.classList.add("transparent");
      }
      this.updateIconColors(iconColorMobile); // Set icon colors for mobile
    }
    this.initIconColors(); // Re-initialize icon colors after class changes
  }

  updateIconColors(iconColor) {
    // Select all icons that need their color changed
    const iconStrokes = this.header.querySelectorAll(".header__icon path");
    const iconFills = this.header.querySelectorAll(
      "#icon-search path, #icon-reset path, #icon-close path",
    );

    // Update stroke colors
    iconStrokes.forEach((path) => {
      path.style.stroke = iconColor;
    });

    // Update fill colors
    iconFills.forEach((path) => {
      path.style.fill = iconColor;
    });
  }

  disconnectedCallback() {
    this.removeEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);
    window.removeEventListener("scroll", this.onScrollHandler);
    window.removeEventListener("resize", this.updateHeaderClass.bind(this));
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });

    observer.observe(this.header);
  }

  initIconColors() {
    const iconStroke = this.header.querySelector(".header__icon path");
    const iconFill = this.header.querySelector(
      "#icon-search path, #icon-reset path, #icon-close path",
    );
    const cartBubble = this.header.querySelector(".cart-count-bubble"); // Assuming there's only one bubble

    if (iconStroke) {
      this.initialStrokeColor = window.getComputedStyle(iconStroke).stroke;
    }
    if (iconFill) {
      this.initialFillColor = window.getComputedStyle(iconFill).fill;
    }
    if (cartBubble) {
      this.initialBubbleBackgroundColor =
        window.getComputedStyle(cartBubble).backgroundColor;
      this.initialBubbleColor = window.getComputedStyle(cartBubble).color;
    }
  }

  detectAndApplyHoverEffects() {
    this.headerWrapper.addEventListener("mouseenter", () => {
      const bgColor = window.getComputedStyle(
        this.headerWrapper,
      ).backgroundColor;
      // Check if the header's background color is transparent
      const isTransparent =
        bgColor === "transparent" || bgColor === "rgba(0, 0, 0, 0)";
      if (isTransparent) {
        this.headerWrapper.classList.add("on-hover-transparent");
        this.applyHoverColor(); // Apply hover color
      }
    });

    this.headerWrapper.addEventListener("mouseleave", () => {
      this.headerWrapper.classList.remove("on-hover-transparent");
      this.resetHoverColor(); // Reset to original color
    });
  }

  applyHoverColor() {
    const strokeColorOnHover = "#131313";
    const fillColorOnHover = "#131313";
    const bubbleColorOnHover = "#FFFFFF";
    const bubbleBgColorOnHover = "#131313";
    this.setIconsColor(
      strokeColorOnHover,
      fillColorOnHover,
      bubbleColorOnHover,
      bubbleBgColorOnHover,
    );
  }

  resetHoverColor() {
    // Reset to the captured initial colors
    this.setIconsColor(
      this.initialStrokeColor,
      this.initialFillColor,
      this.initialBubbleColor,
      this.initialBubbleBackgroundColor,
    );
  }

  setIconsColor(strokeColor, fillColor, bubbleColor, bubbleBackgroundColor) {
    const iconsStroke = this.header.querySelectorAll(".header__icon path");
    const iconsFill = this.header.querySelectorAll(
      "#icon-search path, #icon-reset path, #icon-close path",
    );
    const cartBubbleColor = this.header.querySelectorAll(".cart-count-bubble");

    iconsStroke.forEach((path) => (path.style.stroke = strokeColor));
    iconsFill.forEach((path) => (path.style.fill = fillColor));
    // Update background color of cart bubbles
    cartBubbleColor.forEach((bubble) => {
      bubble.style.backgroundColor = bubbleBackgroundColor; // Sets the background color
      bubble.style.color = bubbleColor;
    });
  }
  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const headerRect = this.header.getBoundingClientRect();
    const headerTop = headerRect.top + scrollTop;

    const viewportWidth = window.innerWidth;
    const isScrollingDown = scrollTop > this.currentScrollTop;
    this.currentScrollTop = scrollTop;
    // this.handleDesktopSearchVisibility();
    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

    if (window.scrollY > 500) {
      this.header.classList.add("scrolled-past-header");
    } else {
      this.header.classList.remove("scrolled-past-header");
    }

    if (isScrollingDown) {
      if (window.scrollY > 100) {
        requestAnimationFrame(this.hide.bind(this));
      }
      if (this.stickyFilter) {
        this.stickyFilter.classList.remove("nav-up");
        if (viewportWidth <= 1024) {
          this.stickyFilter.style.top = `-${this.stickyFilter.offsetHeight}px`;
          this.stickyFilter.style.transition = "top 0.40s ease-out 0.2s";
        } else {
          this.stickyFilter.style.transition = "top 0.30s ease-out";
          this.stickyFilter.style.top = "0";
        }
      }
    } else {
      requestAnimationFrame(this.reveal.bind(this));
      if (this.stickyFilter) {
        this.stickyFilter.classList.remove("nav-down");
        if (!this.header.classList.contains("shopify-section-header-hidden")) {
          this.stickyFilter.style.top = `${this.header.offsetHeight}px`;
        } else {
          this.stickyFilter.style.top = "0px";
        }
        if (viewportWidth <= 1024) {
          this.stickyFilter.style.transition = "top 0.40s ease-in 0.2s";
        } else {
          this.stickyFilter.style.transition = "top 0.30s ease-in";
        }
      }
    }
  }

  showDesktopSearch() {
    if (!this.desktopSearch) return;
    this.desktopSearch.style.opacity = "1";
    this.desktopSearch.style.visibility = "visible";
    this.desktopSearch.style.transform = "translateY(0)";
  }

  // handleDesktopSearchVisibility() {
  //   if (!this.desktopSearch) return;
  //   const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  //   const isAtVeryTop = scrollTop <= 0;
  //   this.desktopSearch.style.transition =
  //     "opacity 0.3s ease, transform 0.3s ease";
  //   if (isAtVeryTop) {
  //     this.desktopSearch.style.opacity = "1";
  //     this.desktopSearch.style.visibility = "visible";
  //     this.desktopSearch.style.transform = "translateY(0)";
  //   } else {
  //     this.desktopSearch.style.opacity = "0";
  //     this.desktopSearch.style.visibility = "hidden";
  //     this.desktopSearch.style.transform = "translateY(-10px)";
  //   }
  // }

  hide() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add(
      "shopify-section-header-hidden",
      "shopify-section-header-sticky",
    );
    document.body.classList.add("header-hidden");
    this.closeMenuDisclosure();
    this.closeSearchModal();
  }

  reveal() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add("shopify-section-header-sticky", "animate");
    this.header.classList.remove("shopify-section-header-hidden");
    document.body.classList.remove("header-hidden");
  }

  reset() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.remove(
      "shopify-section-header-hidden",
      "shopify-section-header-sticky",
      "animate",
    );
    document.body.classList.remove("header-hidden");
  }

  closeMenuDisclosure() {
    this.disclosures =
      this.disclosures || this.header.querySelectorAll("header-menu");
    this.disclosures.forEach((disclosure) => disclosure.close());
  }

  closeSearchModal() {
    this.searchModal =
      this.searchModal || this.header.querySelector("details-modal");
    this.searchModal.close(false);
  }
}

customElements.define("sticky-header", StickyHeader);
